import React, { useEffect, useState, createContext, useContext } from 'react'
import md5 from 'md5'

interface AssetStoreContextType {
  uploadAsset: (file: File) => Promise<{ publicUrl: string; fileName: string; name: string }>
  deleteAssetByName: (name: string) => Promise<void>
}

const AssetStoreContext = createContext<AssetStoreContextType | null>(null)

interface AssetStoreProviderProps {
  children: React.ReactNode
  auth?: {
    getAuthorizationHeader?: () => string
  }
}

// Basic type definitions for the SDK methods we use
interface AssetUploadResult {
  presign: () => Promise<void>
  getUrl: (options: { includeSignature: boolean }) => string
  hide: () => Promise<boolean>
}

interface AssetStoreInstance {
  upload: (options: { file: File; expires: string }) => Promise<AssetUploadResult>
  assets: Array<{
    delete: () => Promise<void>
    data: {
      info: {
        storage: {
          fileName: string
        }
      }
    }
  }>
}

export const AssetStoreProvider: React.FC<AssetStoreProviderProps> = ({ children, auth }) => {
  const [assetStore, setAssetStore] = useState<AssetStoreInstance | null>(null)

  useEffect(() => {
    const authHeader = auth?.getAuthorizationHeader?.()
    if (authHeader) {
      const authorizationProvider = {
        getAuthHeaders: async () => ({
          Authorization: authHeader,
        }),
      }

      const initializeStore = async () => {
        if (typeof window !== 'undefined') {
          try {
            const module = await import('@design-stack-vista/vista-assets-sdk')
            const VistaAssetStoreClass = module.VistaAssetStore
            const SubBrands = module.SubBrands
            const Tenants = module.Tenants

            const newStore = new VistaAssetStoreClass({
              authProvider: authorizationProvider,
              subBrand: SubBrands.VistaPrint,
              experience: 'Email',
              writeTenant: Tenants.VistaCareSupport,
            })
            setAssetStore(newStore as unknown as AssetStoreInstance)
          } catch (error) {
            console.error('Error initializing asset store:', error)
          }
        }
      }

      initializeStore()
    }
  }, [auth])

  const renameFile = (fileName: string) => {
    const dot = fileName.lastIndexOf('.')
    const name = fileName.substring(0, dot).toLowerCase()
    const ext = fileName.substring(dot, fileName.length).toLowerCase()
    return md5(name + Date.now()) + ext
  }

  const uploadAsset = async (file: File): Promise<{ publicUrl: string; fileName: string; name: string }> => {
    if (!assetStore) {
      return {
        publicUrl: `File attachment upload failed: ${file.name}`,
        fileName: file.name,
        name: file.name,
      }
    }

    const fileName = renameFile(file.name)
    try {
      const asset = await assetStore.upload({
        file,
        expires: 'P2Y',
      })
      await asset.presign()
      const publicUrl = asset.getUrl({ includeSignature: true })
      await asset.hide()
      return { publicUrl, fileName, name: file.name }
    } catch (error) {
      console.error('Error uploading asset:', error)
      return {
        publicUrl: `File attachment upload failed: ${file.name}`,
        fileName,
        name: file.name,
      }
    }
  }

  const deleteAssetByName = async (name: string) => {
    if (!assetStore) return

    try {
      const assetToDelete = assetStore.assets.find((asset) => asset.data.info.storage.fileName === name)
      if (assetToDelete) {
        await assetToDelete.delete()
      }
    } catch (error) {
      console.error('Error deleting asset:', error)
    }
  }

  const contextValue: AssetStoreContextType = {
    uploadAsset,
    deleteAssetByName,
  }

  return <AssetStoreContext.Provider value={contextValue}>{children}</AssetStoreContext.Provider>
}

export const useAssetStore = (): AssetStoreContextType | null => {
  return useContext(AssetStoreContext)
}
